import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Tabs,
  TimePicker,
} from 'antd';
import { useMounted } from '../../hooks/useMounted';
import destinationsApi from '../../api/destinations';
import _, { debounce } from 'lodash';
import IntlMessage from '../../components/IntlMessage';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import routeSegmentsApi from '../../api/route-segment';

const { Option } = Select;

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

const RouteSegmentForm = (props) => {
  const {
    open,
    onClose,
    currentData,
    routeId,
    maps,
    setMaps,
    updateMaps,
    indexUpdateSegment,
  } = props;

  const { isMounted } = useMounted();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const intl = useIntl();
  const [destinations, setDestinations] = useState([]);
  const [startLocationId, setStartLocationId] = useState('');
  const [endLocationId, setEndLocationId] = useState('');
  const [startTime, setStartTime] = useState(
    currentData?.start_time && currentData?.start_time !== ' '
      ? dayjs(currentData?.start_time, 'HH:mm:ss').format('HH:mm')
      : ''
  );
  const [endTime, setEndTime] = useState(
    currentData?.end_time && currentData?.end_time !== ' '
      ? dayjs(currentData?.end_time, 'HH:mm:ss').format('HH:mm')
      : ''
  );
  const [destinationsMap, setDestinationsMap] = useState([]);
  const [endDestinationsMap, setEndDestinationsMap] = useState([]);

  const initialValues = {
    ...currentData,
    start_stop_id: currentData?.start_stop?.id ?? null,
    end_stop_id: currentData?.end_stop?.id ?? null,
    status: currentData && currentData.status ? 1 : 0,
    start_time:
      currentData?.start_time && currentData?.start_time !== ' '
        ? dayjs(currentData?.start_time, 'HH:mm:ss')
        : null,
    end_time:
      currentData?.end_time && currentData?.end_time !== ' '
        ? dayjs(currentData?.end_time, 'HH:mm:ss')
        : null,
  };

  useEffect(() => {
    if (currentData?.maps) {
      const currentMap = currentData?.maps?.split(',')?.map((v) => Number(v));
      setMaps(currentMap);
    } else {
      const currentMap = updateMaps?.split(',')?.map((v) => Number(v));
      setMaps(currentMap);
    }
  }, [currentData]);

  const fetch = useCallback(async () => {
    if (isMounted.current) {
      const filter = {
        limit: 9999,
        page: 1,
        order_by: 'title',
        order_dir: 'ASC',
      };

      const destinationRes = await destinationsApi.getAll(filter);
      const destinationData = destinationRes?.data;
      if (Array.isArray(destinationData)) {
        const destinationOptions = destinationData.map((d) => ({
          ...d,
          label: `${d?.title}`,
          value: d?.id,
        }));
        setDestinations(destinationOptions);
      }
      if (Array.isArray(maps)) {
        const result = destinationData.filter((x) => maps.includes(x.id));
        const destinationsMapOption = result.map((d) => ({
          ...d,
          label: `${d?.title}`,
          value: d?.id,
        }));
        const endDestinationsMapOption = result.map((d) => ({
          ...d,
          label: `${d?.title}`,
          value: d?.id,
        }));
        setDestinationsMap(destinationsMapOption);
        setEndDestinationsMap(endDestinationsMapOption);
        // updateMaps
      }
    }
  }, [isMounted, maps]);

  useEffect(() => {
    fetch();
  }, [fetch, currentData]);

  const handleSubmit = () => {
    setLoading(true);
    form
      .validateFields()
      .then((values) => {
        values.start_time = `${startTime}:00`;
        values.end_time = `${endTime}:00`;
        const time_start = dayjs(startTime.trim(), 'HH:mm');
        const time_end = dayjs(endTime.trim(), 'HH:mm');
        const durationInMinutes = time_end.diff(time_start, 'minute');
        const hours = Math.floor(durationInMinutes / 60);
        const minutes = durationInMinutes % 60;
        values.duration = `${hours}:${minutes}`;

        const data = { ...values };
        data['route_id'] = routeId;
        const payload = _.cloneDeep(data);
        if (currentData?.id === indexUpdateSegment) {
          routeSegmentsApi
            .update(currentData.id, payload)
            .then((res) => {
              if (res?.statusCode === 400) {
                Modal.error({
                  title: res?.message,
                  okText: intl.formatMessage({ id: 'global.close' }),
                });
                setLoading(false);
              } else {
                setLoading(false);
                handleCancel();
                props.onReload();
              }
            })
            .catch((error) => {
              console.error(error);
              setLoading(false);
            });
        } else {
          routeSegmentsApi
            .create(payload)
            .then((res) => {
              if (res?.statusCode === 400) {
                Modal.error({
                  title: res?.message,
                  okText: intl.formatMessage({ id: 'global.close' }),
                });
                setLoading(false);
              } else {
                setLoading(false);
                handleCancel();
                props.onReload();
              }
            })
            .catch((error) => {
              console.error(error);
              if (error?.data?.statusCode === 400) {
                Modal.error({
                  title: error?.data?.message,
                  okText: intl.formatMessage({ id: 'global.close' }),
                });
              }
              setLoading(false);
            });
        }
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  };

  const onFinishFailed = (errorInfo) => {
    console.error('Failed:', errorInfo);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSearchDestination = debounce(async (value) => {
    try {
      const payload = {
        page: 1,
        limit: value === '' ? 10 : 9999,
        keyword: value || '',
        order_by: 'title',
        order_dir: 'ASC',
      };
      const res = await destinationsApi.getAll(payload);
      const destinationData = res?.data;
      if (Array.isArray(destinationData)) {
        const destinationOptions = destinationData.map((d) => ({
          ...d,
          label: `${d?.title}`,
          value: d?.id,
        }));
        setDestinations(destinationOptions);
      }
    } catch (error) {
      console.error(error);
    }
  }, 300);

  const onChangeStartLocation = (value) => {
    setStartLocationId(value);
  };

  const onChangeEndLocation = (value) => {
    setEndLocationId(value);
  };

  const onChangeStartTime = (time, timeString) => {
    setStartTime(timeString);
  };

  const onChangeEndTime = (time, timeString) => {
    setEndTime(timeString);
  };

  const tabItems = [
    {
      key: '1',
      label: <IntlMessage id="global.segment" />,
      children: (
        <React.Fragment>
          <Form.Item
            name="start_stop_id"
            label={<IntlMessage id="global.start_location" />}
            rules={[
              {
                required: true,
                message: <IntlMessage id="global.requiredfield" />,
              },
            ]}>
            <Select
              showSearch
              onChange={onChangeStartLocation}
              filterOption={false}
              onSearch={handleSearchDestination}
              allowClear
              placeholder={intl.formatMessage({
                id: 'placeholder.select_start_location',
              })}>
              {destinationsMap
                .filter((item) => item.value !== endLocationId)
                .map((item, index) => {
                  return (
                    <Option value={item.value} key={index}>
                      {item.label}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
          <Form.Item
            name="end_stop_id"
            label={<IntlMessage id="global.end_location" />}
            rules={[
              {
                required: true,
                message: <IntlMessage id="global.requiredfield" />,
              },
            ]}>
            <Select
              showSearch
              onChange={onChangeEndLocation}
              filterOption={false}
              allowClear
              onSearch={handleSearchDestination}
              placeholder={intl.formatMessage({
                id: 'placeholder.select_end_location',
              })}>
              {endDestinationsMap
                .filter((item) => item.id !== startLocationId)
                .map((item, index) => {
                  return (
                    <Option value={item.value} key={index}>
                      {item.label}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
          <Form.Item
            label={<IntlMessage id="global.start_time" />}
            name="start_time"
            rules={[
              {
                required: true,
                message: <IntlMessage id="global.requiredfield" />,
              },
            ]}>
            <TimePicker
              onChange={onChangeStartTime}
              showSecond={false}
              showMillisecond={false}
            />
          </Form.Item>
          <Form.Item
            label={<IntlMessage id="global.end_time" />}
            name="end_time"
            rules={[
              {
                required: true,
                message: <IntlMessage id="global.requiredfield" />,
              },
            ]}>
            <TimePicker
              onChange={onChangeEndTime}
              showSecond={false}
              showMillisecond={false}
            />
          </Form.Item>
          <Form.Item label={<IntlMessage id="global.status" />} name="status">
            <Radio.Group>
              <Radio value={0}>
                <IntlMessage id="global.inactive" />
              </Radio>
              <Radio value={1}>
                <IntlMessage id="global.active" />
              </Radio>
            </Radio.Group>
          </Form.Item>
        </React.Fragment>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Modal
        title={<IntlMessage id="global.add" />}
        open={open}
        destroyOnClose={true}
        onCancel={handleCancel}
        width="80%"
        onOk={handleSubmit}
        footer={[
          <Button key="back" type="default" onClick={handleCancel}>
            {<IntlMessage id="global.cancel" />}
          </Button>,
          <Button
            key="submit"
            type="primary"
            htmlType="submit"
            loading={loading}
            onClick={handleSubmit}>
            {<IntlMessage id="global.save" />}
          </Button>,
        ]}>
        <Form
          {...layout}
          form={form}
          initialValues={initialValues}
          labelAlign="left"
          onFinish={handleSubmit}
          onFinishFailed={onFinishFailed}>
          <Tabs defaultActiveKey="1" items={tabItems}></Tabs>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default RouteSegmentForm;
